import {getScoreKeyNameFromScorerRef} from '../../CompareEvaluationsPage/ecpUtil';
import {BooleanSummary, NumericSummary, ScorerRefMap, ScoreTree} from './types';

export const isNumericSummary = (value: unknown): value is NumericSummary => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'mean' in value &&
    typeof (value as NumericSummary).mean === 'number' &&
    Object.keys(value).length === 1
  );
};

export const isBooleanSummary = (value: unknown): value is BooleanSummary => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'true_count' in value &&
    'true_fraction' in value &&
    typeof (value as BooleanSummary).true_count === 'number' &&
    typeof (value as BooleanSummary).true_fraction === 'number' &&
    Object.keys(value).length === 2
  );
};

export const formatScoreValue = (value: number | boolean): string => {
  if (typeof value === 'boolean') {
    return value ? '✓' : '✗';
  }

  if (Number.isInteger(value)) {
    return value.toString();
  }

  return value.toFixed(3);
};

type ScoreNode =
  | ScoreTree
  | number
  | boolean
  | NumericSummary
  | BooleanSummary
  | null;

const validateAndPruneScoreNode = (value: unknown): ScoreNode => {
  // Reject arrays - they cause traversal explosion in hasAnySummaryDescendant
  if (Array.isArray(value)) {
    return null;
  }

  // Accept leaf values
  if (typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }

  // Accept summary objects
  if (isNumericSummary(value) || isBooleanSummary(value)) {
    return value;
  }

  // Recursively clean nested objects
  if (typeof value === 'object' && value !== null) {
    const sanitized: ScoreTree = {};
    let hasValidChildren = false;
    for (const [k, v] of Object.entries(value)) {
      const cleanValue = validateAndPruneScoreNode(v);
      if (cleanValue !== null) {
        sanitized[k] = cleanValue;
        hasValidChildren = true;
      }
    }
    return hasValidChildren ? sanitized : null;
  }

  return null;
};

export const buildScoreTree = (
  output: Record<string, unknown> | null | undefined
): ScoreTree => {
  const tree: ScoreTree = {};

  if (!output) {
    return tree;
  }

  Object.entries(output).forEach(([key, value]) => {
    if (key.startsWith('_') || key === 'model_latency') return;
    const node = validateAndPruneScoreNode(value);
    if (node !== null) {
      tree[key] = node;
    }
  });

  return tree;
};

export const filterScoreTree = (
  scoreTree: ScoreTree,
  filterText: string
): ScoreTree => {
  if (!filterText) {
    return scoreTree;
  }

  try {
    const regex = new RegExp(filterText, 'i');
    const filtered: ScoreTree = {};

    const filterTree = (obj: unknown, currentPath: string): unknown => {
      if (typeof obj === 'number' || typeof obj === 'boolean') {
        return regex.test(currentPath) ? obj : null;
      }

      if (typeof obj === 'object' && obj !== null && !Array.isArray(obj)) {
        const result: ScoreTree = {};
        let hasMatches = false;

        Object.entries(obj).forEach(([key, val]) => {
          const newPath = currentPath ? `${currentPath}.${key}` : key;
          const filteredVal = filterTree(val, newPath);
          if (filteredVal !== null) {
            result[key] = filteredVal as number | boolean | ScoreTree;
            hasMatches = true;
          }
        });

        return hasMatches ? result : null;
      }

      return null;
    };

    Object.entries(scoreTree).forEach(([key, value]) => {
      const filteredVal = filterTree(value, key);
      if (filteredVal !== null) {
        filtered[key] = filteredVal as number | boolean | ScoreTree;
      }
    });

    return filtered;
  } catch {
    return scoreTree;
  }
};

export const buildScorerRefMap = (scorers: string[]): ScorerRefMap => {
  const map: ScorerRefMap = {};

  scorers.forEach((scorerRef: string) => {
    const scorerName = getScoreKeyNameFromScorerRef(scorerRef);
    if (scorerName) {
      map[scorerName] = scorerRef;
    }
  });

  return map;
};
